import { useContext, useEffect, useMemo, useState } from 'react'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { Alert, Box, CircularProgress, Grid, IconButton, InputBase, Typography, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import { FolderTreeContext } from '../../providers/folder-tree-provider'
import Api from '../../utils/api'
import useIsMobile from '../../utils/hooks/useIsMobile'
import FullScreenModal from '../full-screen-modal'

import Breadcrumbs from './breadcrumbs'
import FilterChip from './filter-chip'
import FolderRow from './folder-row'
import { fetchParticipantFolders, getChildren } from './helpers'
import useStyles from './index.styles'
import { ILocationFolder, LocationFilter } from './types'

const MIN_SEARCH_LENGTH = 3

const LOCATION_FILTERS: LocationFilter[] = ['all', 'alerting', 'incomplete']

interface IProps {
  open: boolean
  onClose: () => void
  onSelect: (folderId: number) => void
  selectedFolderId: number | null | undefined
}

const LocationSelectModal = (props: IProps) => {
  const { open, onClose, onSelect, selectedFolderId } = props

  const classes = useStyles
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const { folderTree, error: hasTreeError } = useContext(FolderTreeContext)

  // True once the single-root participant check has finished, so the plain tree isn't shown while it runs
  const [hasLookedUpRoot, setHasLookedUpRoot] = useState<boolean>(false)
  // Loading that happens over an already-rendered list
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentParentId, setCurrentParentId] = useState<number | null>(null)
  const [navigationTrail, setNavigationTrail] = useState<ILocationFolder[]>([])
  // Parent first, followed by its child participants
  const [participantFolders, setParticipantFolders] = useState<ILocationFolder[] | null>(null)
  // Statuses learned from participant lists, so ancestors in the tree can reflect their descendants
  const [knownStatuses, setKnownStatuses] = useState<Record<number, Pick<ILocationFolder, 'hasAlerts' | 'isIncomplete'>>>({})

  const participantParent = participantFolders?.[0]

  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all')

  const hasChildren = (folderId: number) => !!folderTree?.tree[folderId]?.children?.length

  // A folder shows a status when it or any folder beneath it has one
  const getStatus = (folderId: number): Pick<ILocationFolder, 'hasAlerts' | 'isIncomplete'> => {
    const status = { hasAlerts: !!knownStatuses[folderId]?.hasAlerts, isIncomplete: !!knownStatuses[folderId]?.isIncomplete }
    folderTree?.tree[folderId]?.children?.forEach((childId) => {
      const childStatus = getStatus(childId)
      status.hasAlerts ||= !!childStatus.hasAlerts
      status.isIncomplete ||= !!childStatus.isIncomplete
    })
    return status
  }

  // The query stays applied while the field is hidden; it only filters once it has enough characters
  const trimmedQuery = searchQuery.trim().toLowerCase()
  const isSearchActive = trimmedQuery.length >= MIN_SEARCH_LENGTH
  const matchesSearch = (folder: ILocationFolder) => !isSearchActive || folder.name.toLowerCase().includes(trimmedQuery)
  const matchesFilter = (folder: ILocationFolder) => {
    if (locationFilter === 'alerting') return !!folder.hasAlerts
    if (locationFilter === 'incomplete') return !!folder.isIncomplete
    return true
  }

  const allVisibleFolders = useMemo(() => getChildren(folderTree, currentParentId), [folderTree, currentParentId])
  const showParticipantParent = !!participantParent && matchesSearch(participantParent) && matchesFilter(participantParent)
  // Only one list is shown at a time: the participant's children, or the folders at the current tree level.
  // slice(1) skips the participant parent (always first), which is rendered and searched separately from its children
  const listedFolders = participantParent ? (participantFolders ?? []).slice(1) : allVisibleFolders
  const visibleFolders = listedFolders.filter(matchesSearch).filter((folder) => !participantParent || matchesFilter(folder))
  const hasNoResults = (isSearchActive || (!!participantParent && locationFilter !== 'all')) && !showParticipantParent && visibleFolders.length === 0

  // The only root is a participant, so its list has no tree to navigate back to
  const hasSingleRootParticipant = folderTree?.roots.length === 1 && participantParent?.id === folderTree.roots[0]

  const isCurrentlyViewingFolder = (folderId: number) => folderId === selectedFolderId

  // Covers the wait for the shared tree as well as the single-root participant lookup
  const isInitialLoading = !hasTreeError && (!folderTree || (folderTree.roots.length === 1 && !hasLookedUpRoot))

  useEffect(() => {
    // Reset so a reopened modal or a new tree waits for the check again
    setHasLookedUpRoot(false)
    setNavigationTrail([])
    setSearchQuery('')
    setIsSearchOpen(false)
    setLocationFilter('all')
    if (!open || !folderTree) return

    // With a single root that is a participant folder, list the participant and its children flat
    const getParticipantRoot = async () => {
      setParticipantFolders(null)
      setCurrentParentId(null)
      if (folderTree.roots.length !== 1) return

      try {
        const participants = await fetchParticipantFolders(folderTree.roots[0])
        if (participants) setParticipantFolders(participants)
      } catch {
        // Fall back to showing the folder tree
      }
      setHasLookedUpRoot(true)
    }

    getParticipantRoot()
  }, [open, folderTree])

  // Search and status filter only apply to the list they were entered on, so navigating clears them
  const resetSearchAndFilter = () => {
    setSearchQuery('')
    setLocationFilter('all')
  }

  // Participants list directly; any other folder type drills further down the tree
  const handleDrillDown = async (folder: ILocationFolder) => {
    setIsLoading(true)
    const participants = await fetchParticipantFolders(folder.id)
    setNavigationTrail((previousTrail) => [...previousTrail, folder])
    resetSearchAndFilter()
    if (participants) {
      setParticipantFolders(participants)
      setKnownStatuses((prev) => ({
        ...prev,
        ...Object.fromEntries(participants.map(({ id, hasAlerts, isIncomplete }) => [id, { hasAlerts, isIncomplete }])),
      }))
    } else setCurrentParentId(folder.id)
    setIsLoading(false)
  }

  const handleHome = () => {
    setParticipantFolders(null)
    setCurrentParentId(null)
    setNavigationTrail([])
    resetSearchAndFilter()
  }

  const handleBreadcrumbClick = (index: number) => {
    // Clicking the last breadcrumb does nothing since we are already viewing that folder
    if (index === navigationTrail.length - 1) return

    setNavigationTrail(navigationTrail.slice(0, index + 1))
    setParticipantFolders(null)
    resetSearchAndFilter()
    setCurrentParentId(navigationTrail[index].id)
  }

  const handleLocationSelect = (selectedFolder: ILocationFolder) => {
    onSelect(selectedFolder.id)
    onClose()
    if (selectedFolderId) {
      enqueueSnackbar(`Viewing: ${selectedFolder.name}`)
    }
  }

  // Tree rows carry no folder type, so confirm it's a participant before selecting
  const handleTreeSelect = async (folder: ILocationFolder) => {
    setIsLoading(true)
    const res = await Api.get(`/api/folders/${folder.id}`)
    setIsLoading(false)

    if (res.ok && (res.body as IFolderType).type === 'Participant') {
      handleLocationSelect(folder)
    } else if (res.ok && hasChildren(folder.id)) {
      setNavigationTrail((previousTrail) => [...previousTrail, folder])
      resetSearchAndFilter()
      setCurrentParentId(folder.id)
    } else {
      enqueueSnackbar(res.ok ? 'This folder type cannot be selected' : 'Could not select that location. Please try again', { variant: 'error' })
    }
  }

  return (
    <FullScreenModal
      open={open}
      onClose={onClose}
      disableBackdropClick={!selectedFolderId}
      title="Select Location"
      showCloseIcon={!!selectedFolderId}
      rightIcon={
        <IconButton
          aria-label="search locations"
          aria-expanded={isSearchOpen}
          onClick={() => setIsSearchOpen((previous) => !previous)}
          sx={classes.searchToggle(theme, isSearchOpen)}
        >
          <SearchIcon />
        </IconButton>
      }
      maxWidth="sm"
      contentContainerSx={isMobile ? classes.modalContent : classes.desktopModalContent}
      desktopPaperSx={classes.desktopPaper}
    >
      <Grid container={true} spacing={0} sx={classes.container}>
        {isInitialLoading && <CircularProgress sx={classes.spinner} data-testid="spinner" />}

        {!isInitialLoading && hasTreeError && <Alert severity="error">Could not load locations. Please try again later</Alert>}

        {!isInitialLoading && !hasTreeError && (
          <>
            {isLoading && (
              <Grid container={true} sx={classes.loadingOverlay} data-testid="loading-overlay">
                <CircularProgress />
              </Grid>
            )}

            {isSearchOpen && (
              <Box sx={classes.searchContainer}>
                <InputBase
                  autoFocus={true}
                  fullWidth={true}
                  placeholder="Filter locations"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  inputProps={{ 'aria-label': 'filter locations' }}
                  sx={classes.searchInput}
                  startAdornment={<SearchIcon sx={classes.searchIcon} />}
                  endAdornment={
                    <IconButton aria-label="clear search" size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" sx={classes.clearIcon} />
                    </IconButton>
                  }
                />
              </Box>
            )}

            {participantParent && (
              <Grid container={true} component="div" role="group" aria-label="Filter locations by status" sx={classes.filterContainer}>
                {LOCATION_FILTERS.map((filter) => (
                  <FilterChip key={filter} filter={filter} isActive={locationFilter === filter} onSelect={setLocationFilter} />
                ))}
              </Grid>
            )}

            <Box sx={classes.treeScroll}>
              {hasNoResults && (
                <Typography variant="body2" sx={classes.noResultsText}>
                  Nothing found, please try again
                </Typography>
              )}
              <Box sx={classes.tree} hidden={hasNoResults}>
                {!hasSingleRootParticipant && <Breadcrumbs navigationTrail={navigationTrail} onHome={handleHome} onSelect={handleBreadcrumbClick} />}
                {participantParent && (
                  <>
                    {showParticipantParent && (
                      <FolderRow
                        folder={participantParent}
                        isSelected={isCurrentlyViewingFolder(participantParent.id)}
                        onClick={() => handleLocationSelect(participantParent)}
                      />
                    )}

                    <Box sx={classes.childList}>
                      {visibleFolders.map((child, index) => (
                        <Grid key={child.id} container={true} sx={classes.childRow(theme, index === visibleFolders.length - 1)}>
                          <FolderRow folder={child} isSelected={isCurrentlyViewingFolder(child.id)} onClick={() => handleLocationSelect(child)} />
                        </Grid>
                      ))}
                    </Box>
                  </>
                )}

                {!participantParent && !isSearchActive && visibleFolders.length === 0 && (
                  <Typography variant="body2" color="textSecondary" sx={classes.emptyText}>
                    No locations found
                  </Typography>
                )}

                {!participantParent &&
                  visibleFolders.map((folder) => (
                    <Grid key={folder.id} container={true} sx={classes.folderRowWrapper}>
                      <FolderRow
                        folder={{ ...folder, ...getStatus(folder.id) }}
                        isSelected={isCurrentlyViewingFolder(folder.id)}
                        onClick={() => handleTreeSelect(folder)}
                      />

                      {hasChildren(folder.id) && (
                        <IconButton aria-label={`View ${folder.name} locations`} onClick={() => handleDrillDown(folder)}>
                          <ChevronRightIcon sx={classes.chevron} />
                        </IconButton>
                      )}
                    </Grid>
                  ))}
              </Box>
            </Box>
          </>
        )}
      </Grid>
    </FullScreenModal>
  )
}

export default LocationSelectModal
